import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import { FiCheck, FiChevronDown, FiChevronRight, FiSearch } from "react-icons/fi";

const KEY = "company.users.delegate";

const normalize = (text) => (text || "").toLocaleLowerCase("tr");

const toPerson = (id, name, title, companyName) => ({
  userId: id,
  name,
  detail: title || companyName || "",
  title: title || "",
});

// Groups and companies are expandable header rows; only people can be selected
const buildOptions = ({ candidates, sourceId, expanded, search, translate }) => {
  const term = normalize(search);
  const matches = (text) => !term || normalize(text).includes(term);
  const t = (key, data) => translate(`${KEY}.${key}`, data);

  const seen = new Set();
  const users = candidates.users
    .filter((user) => user.is_action_user && user.user_id !== sourceId)
    .filter((user) => !seen.has(user.user_id) && seen.add(user.user_id))
    .filter((user) => matches(user.user_name))
    .map((user) => ({
      kind: "person",
      value: `u:${user.user_id}`,
      ...toPerson(user.user_id, user.user_name, user.title, user.company_name),
    }));

  const scopeOptions = (items, type) =>
    items.flatMap((item) => {
      const members = (item.users || [])
        .filter((user) => user.is_action_user && user.id !== sourceId)
        .map((user) => ({
          kind: "person",
          indent: true,
          value: `${type}${item.id}:${user.id}`,
          ...toPerson(user.id, user.name, user.title, user.company_name),
        }));
      if (!members.length) return [];

      const nameMatches = matches(item.name);
      const visibleMembers = nameMatches ? members : members.filter((member) => matches(member.name));
      if (!nameMatches && !visibleMembers.length) return [];

      const key = `${type}:${item.id}`;
      const isOpen = term ? true : expanded.has(key);
      const header = {
        kind: "scope",
        value: key,
        name: item.name,
        detail: `(${t("members_count", { count: members.length })})`,
        isOpen,
      };
      return isOpen ? [header, ...visibleMembers] : [header];
    });

  return [
    { label: translate("users"), options: users },
    { label: translate("groups"), options: scopeOptions(candidates.groups, "group") },
    { label: translate("companies"), options: scopeOptions(candidates.companies, "company") },
  ].filter((section) => section.options.length);
};

const Option = (props) => {
  const { data, isSelected } = props;
  if (data.kind === "scope") {
    return (
      <components.Option {...props} className={data.isOpen ? "is-open-scope" : "is-closed-scope"}>
        <div className="d-flex align-items-center gap-2 fw-semibold">
          {data.isOpen ? <FiChevronDown className="scope-arrow" /> : <FiChevronRight className="scope-arrow" />}
          <span>{data.name}</span>
          <span className="text-secondary fw-normal">{data.detail}</span>
        </div>
      </components.Option>
    );
  }
  return (
    <components.Option {...props} className={isSelected ? "is-selected-person" : ""}>
      <div className={`d-flex align-items-center gap-2 ${data.indent ? "ps-4" : ""}`}>
        {isSelected ? <FiCheck className="text-orange" /> : <span className="delegate-target-dot" />}
        <span className={isSelected ? "fw-bold" : ""}>{data.name}</span>
        {data.detail && <span className="text-secondary">— {data.detail}</span>}
      </div>
    </components.Option>
  );
};

const MultiValueLabel = (props) => (
  <components.MultiValueLabel {...props}>
    <span className="fw-semibold">{props.data.name}</span>
    {props.data.detail && <span className="fw-normal"> · {props.data.detail}</span>}
  </components.MultiValueLabel>
);

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <FiSearch className="fs-5" />
  </components.DropdownIndicator>
);

const DelegateTargetPicker = ({ candidates, sourceId, value, onChange, disabled, translate }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const options = useMemo(
    () => buildOptions({ candidates, sourceId, expanded, search, translate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidates, sourceId, expanded, search]
  );

  const toggleScope = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const handleChange = (selected, meta) => {
    if (meta.action === "select-option") {
      const option = meta.option;
      if (option.kind === "scope") {
        toggleScope(option.value);
        return;
      }
      onChange({ value: option.userId, label: option.name, name: option.name, detail: option.detail });
      setSearch("");
      setMenuOpen(false);
      return;
    }
    if (meta.action === "remove-value" || meta.action === "clear" || meta.action === "pop-value") {
      onChange(null);
    }
  };

  return (
    <Select
      isMulti
      isClearable={false}
      backspaceRemovesValue
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      menuIsOpen={menuOpen}
      maxMenuHeight={224}
      onMenuOpen={() => setMenuOpen(true)}
      onMenuClose={() => setMenuOpen(false)}
      inputValue={search}
      onInputChange={(text, meta) => {
        if (meta.action === "input-change") setSearch(text);
      }}
      options={options}
      value={value ? [{ ...value, userId: value.value, value: `selected:${value.value}` }] : []}
      onChange={handleChange}
      filterOption={() => true}
      isOptionSelected={(option) => option.kind === "person" && option.userId === value?.value}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.name}
      components={{ Option, MultiValueLabel, DropdownIndicator, IndicatorSeparator: null }}
      placeholder={translate(`${KEY}.target_placeholder`)}
      noOptionsMessage={() => translate(`${KEY}.no_target_found`)}
      isDisabled={disabled}
      classNamePrefix="delegate-target"
      className="DelegateTargetPicker"
      styles={{ menu: (base) => ({ ...base, zIndex: 99 }) }}
    />
  );
};

DelegateTargetPicker.propTypes = {
  candidates: PropTypes.shape({
    users: PropTypes.array,
    groups: PropTypes.array,
    companies: PropTypes.array,
  }).isRequired,
  sourceId: PropTypes.number,
  value: PropTypes.shape({ value: PropTypes.number, label: PropTypes.string, detail: PropTypes.string }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  translate: PropTypes.func.isRequired,
};

export default DelegateTargetPicker;
